import { Fragment, useState } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ApptPill, PANEL, PathwayBadge, TableHead } from "./shared";

const LEAD_STATUSES = [["new", "New"], ["contacted", "Contacted"], ["booked", "Booked"], ["completed", "Completed"], ["not_interested", "Not Interested"]];

export function CheckupResultsTable({ checkups }) {
  return (
    <div className={PANEL} data-testid="admin-checkups">
      <h2 className="font-serif text-2xl font-semibold text-navy mb-1.5">Checkup Results</h2>
      <p className="text-sm text-slate-500 mb-4">Every completed checkup across all four pathways — score or primary interest, plus contact details when the visitor shared them.</p>
      <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full text-sm min-w-[760px]">
          <TableHead columns={["Date", "Pathway", "Score", "Category / Interest", "Name", "Email", "Source", "Appt."]} />
          <tbody>
            {checkups.map((c) => (
              <tr key={c.id} className="border-b border-sand/60 last:border-0">
                <td className="py-3 pr-4 text-slate-500 text-xs">{new Date(c.date).toLocaleString()}</td>
                <td className="py-3 pr-4"><PathwayBadge pathway={c.pathway} label={c.pathway_label} /></td>
                <td className="py-3 pr-4 font-bold text-navy">{c.score ?? "—"}</td>
                <td className="py-3 pr-4 text-slate-600 text-xs">{c.band || c.primary_concern || "—"}</td>
                <td className="py-3 pr-4 font-semibold text-navy">{c.name || <span className="text-slate-400 font-normal">Anonymous</span>}</td>
                <td className="py-3 pr-4 text-slate-600">{c.email || "—"}</td>
                <td className="py-3 pr-4 text-slate-600">{c.referral_source || c.utm_source || "Direct"}</td>
                <td className="py-3"><ApptPill status={c.appointment_status} /></td>
              </tr>
            ))}
            {checkups.length === 0 && (
              <tr><td colSpan={8} className="py-8 text-center text-slate-500">No completed checkups yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function statusClass(status) {
  if (status === "new") return "bg-gold-light text-gold border-sand";
  if (status === "completed" || status === "booked") return "bg-[#E8F5EC] text-[#15803D] border-[#BEE3CB]";
  return "bg-white text-navy border-sand";
}

function LeadContextRow({ lead }) {
  const context = lead.crm_context || [];
  return (
    <tr className="border-b border-sand/60 bg-cream/70" data-testid="lead-context-row">
      <td colSpan={9} className="py-3 px-4">
        <div className="flex flex-wrap gap-2">
          {context.map((line, i) => (
            <span key={line} className={`text-xs rounded-full px-3 py-1.5 border ${i === 0 ? "bg-navy text-cream border-navy font-bold" : "bg-white text-navy border-sand"}`}>{line}</span>
          ))}
          {context.length === 0 && <span className="text-xs text-slate-500">No additional context recorded for this lead.</span>}
        </div>
        {(lead.tags || []).length > 0 && (
          <p className="mt-2 text-[11px] text-slate-500"><span className="font-bold uppercase tracking-wider">Tags:</span> {lead.tags.join(" · ")}</p>
        )}
      </td>
    </tr>
  );
}

export function LeadsTable({ leads, onLeadsChange, onRefresh }) {
  const [expanded, setExpanded] = useState({});

  const setStatus = async (lead, status) => {
    onLeadsChange((cur) => cur.map((x) => (x.id === lead.id ? { ...x, lead_status: status } : x)));
    try {
      await api.put(`/admin/leads/${lead.id}/status`, { status });
      onRefresh();
    } catch {
      toast.error("Could not update status.");
    }
  };

  return (
    <div className={PANEL} data-testid="admin-leads">
      <h2 className="font-serif text-2xl font-semibold text-navy mb-1.5">Recent Leads</h2>
      <p className="text-sm text-slate-500 mb-4">Tap a name to see the context the visitor shared — so you know which conversation you're entering before you call.</p>
      <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full text-sm min-w-[820px]">
          <TableHead columns={["Name", "Contact", "Pathway", "Score", "Source", "Concern", "Appt.", "Status", "Date"]} />
          <tbody>
            {leads.map((l) => (
              <Fragment key={l.id}>
                <tr className="border-b border-sand/60 last:border-0">
                  <td className="py-3 pr-4 font-semibold text-navy">
                    <button onClick={() => setExpanded((e) => ({ ...e, [l.id]: !e[l.id] }))} className="text-left hover:text-gold transition-colors inline-flex items-center gap-1.5" data-testid="lead-context-toggle">
                      {l.first_name} {l.last_name}
                      <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${expanded[l.id] ? "rotate-180" : ""}`} />
                    </button>
                  </td>
                  <td className="py-3 pr-4 text-slate-600">{l.email}<br /><span className="text-xs">{l.phone}</span></td>
                  <td className="py-3 pr-4"><PathwayBadge pathway={l.pathway} label={l.pathway_label || l.source} /></td>
                  <td className="py-3 pr-4"><span className="font-bold text-navy">{l.score ?? "—"}</span></td>
                  <td className="py-3 pr-4 text-slate-600">{l.referral_source || l.utm_source || "Direct"}</td>
                  <td className="py-3 pr-4 text-slate-600 text-xs">{l.primary_concern || "—"}</td>
                  <td className="py-3 pr-4"><ApptPill status={l.appointment_status} /></td>
                  <td className="py-3 pr-4">
                    <select
                      value={l.lead_status || "new"}
                      onChange={(e) => setStatus(l, e.target.value)}
                      className={`text-xs font-bold rounded-full border px-2.5 py-1.5 cursor-pointer ${statusClass(l.lead_status || "new")}`}
                      data-testid="lead-status-select"
                    >
                      {LEAD_STATUSES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                    </select>
                  </td>
                  <td className="py-3 text-slate-500 text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                </tr>
                {expanded[l.id] && <LeadContextRow lead={l} />}
              </Fragment>
            ))}
            {leads.length === 0 && (
              <tr><td colSpan={9} className="py-8 text-center text-slate-500">No leads yet — share your Legacy Checkup link to get started.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
